import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useToggle } from 'react-use';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  DashboardIcon,
  DeliveryIcon,
  FlashSaleIcon,
  PlusIcon,
  PurchaseIcon,
  SellThroughRateIcon,
  TagNewIcon,
} from 'src/svgs';
import { SaleIcon } from 'src/svgs/SaleIcon';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CompListing,
  EventWithData,
  Feature,
  Listing,
  SellerEventMarketplaceEventInfo,
  UserSetting,
} from 'src/WebApiController';

import { InventoryEventTabs } from '../InventoryEventTabs';
import { useMarketplaceEventInfo } from './InventoryEventLocalContext.hooks';

type EventContent = {
  id: InventoryEventTabs;
  Icon: React.ComponentType<Omit<PosIconProps, 'viewBox'>>;
  contentId: ContentId;
  useStroke?: boolean;
  mobileOnly?: boolean;
  hide?: boolean;
};

export type IInventoryEventLocalContext = {
  defaultTab?: InventoryEventTabs;
  activeTab: InventoryEventTabs;
  eventContents: EventContent[];
  marketplaceEventInfo?: SellerEventMarketplaceEventInfo[];
  listings: Listing[];
  compListings: CompListing[];
  showMap: boolean;
  toggleShowMap: (showMap: boolean) => void;
  setActiveTab: (tab: InventoryEventTabs) => void;
  refetchMarketplaceEventInfo: () => void;
  onUpdateDefaultType: (type: InventoryEventTabs) => void;
};

export const InventoryEventLocalContext =
  createContext<IInventoryEventLocalContext>({
    activeTab: InventoryEventTabs.Info,
    eventContents: [],
    listings: [],
    compListings: [],
    showMap: true,
    toggleShowMap: noop,
    setActiveTab: noop,
    refetchMarketplaceEventInfo: noop,
    onUpdateDefaultType: noop,
  });

export const useInventoryEventLocalContext = () =>
  useContext(InventoryEventLocalContext);

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
export const InventoryEventLocalContextProvider = ({
  children,
  accordionItemProps,
}: PropsWithChildren & {
  accordionItemProps?: EventWithData;
}) => {
  const hasMobileInventoryPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const isMobileLayout =
    useMatchMedia('mobile') && hasMobileInventoryPageFeature;

  const contentTypeInitRef = useRef<boolean>(false);

  // Move the initial tab calculation into useState's lazy initialization
  const [activeTab, setActiveTab] = useState<InventoryEventTabs>(
    InventoryEventTabs.Info
  );

  // Used for mobile App
  const [showMap, toggleShowMap] = useToggle(true);

  const hasPricingV2DemandDashboard = useUserHasFeature(
    Feature.PricingV2DemandDashboard
  );
  const hasMobileInventoryEventPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const hasPricingABTestFeature = useUserHasFeature(Feature.PricingABTest);
  const hasFulfillmentRulesEventLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesEventLevel
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useMarketplaceEventInfo(accordionItemProps?.event!);

  const {
    value: defaultInventoryTabUserSetting,
    setUserSetting: setDefaultInventoryTabUserSetting,
  } = useServerUserSetting<InventoryEventTabs>({
    id: UserSetting.InventoryDefaultTab,
  });

  const { onUpdateInventoryTab, isOnSidePanel, inventoryTab, enabled } =
    useEventHubContext();

  const listings = useMemo(
    () => accordionItemProps?.entities?.listings ?? [],
    [accordionItemProps?.entities?.listings]
  );
  const { compListingsQuery } = useGetCompListings(accordionItemProps?.event);

  const onSetInventoryTab = useCallback(
    (tab: InventoryEventTabs) => {
      setActiveTab(tab);
      if (enabled && !isOnSidePanel) {
        onUpdateInventoryTab(tab);
      }
    },
    [enabled, isOnSidePanel, onUpdateInventoryTab]
  );

  useEffect(() => {
    if (isOnSidePanel && inventoryTab) {
      setActiveTab(inventoryTab);
    }
  }, [enabled, inventoryTab, isOnSidePanel]);

  useEffect(() => {
    if (
      !isMobileLayout &&
      defaultInventoryTabUserSetting &&
      contentTypeInitRef.current === false &&
      Object.values(InventoryEventTabs).includes(defaultInventoryTabUserSetting)
    ) {
      if (defaultInventoryTabUserSetting === InventoryEventTabs.None) {
        setActiveTab(InventoryEventTabs.Info);
      } else {
        setActiveTab(defaultInventoryTabUserSetting);
      }
      contentTypeInitRef.current = true;
    }
  }, [defaultInventoryTabUserSetting, isMobileLayout]);

  const eventContents = useMemo(() => {
    const contents: EventContent[] = [
      {
        id: InventoryEventTabs.Info,
        Icon: DashboardIcon,
        contentId: ContentId.Inventory,
      },
    ];

    if (hasPricingV2DemandDashboard && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Dashboard,
        Icon: DashboardIcon,
        contentId: ContentId.Dashboard,
      });
    }

    if (hasPricingABTestFeature && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.PricingABTest,
        Icon: FlashSaleIcon,
        contentId: ContentId.FlashSale,
      });
    }

    if (
      hasFulfillmentRulesEventLevelFeature ||
      accordionItemProps?.event?.viagId != null
    ) {
      contents.push({
        id: InventoryEventTabs.Fulfillment,
        Icon: DeliveryIcon,
        contentId: ContentId.Delivery,
      });
    }

    if (hasAutoPricingFeature) {
      contents.push({
        id: InventoryEventTabs.SellThroughRate,
        Icon: SellThroughRateIcon,
        contentId: ContentId.SellThroughRate,
      });
    }
    if (!hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Tags,
        Icon: TagNewIcon,
        contentId: ContentId.Tags,
        useStroke: true,
      });
    }
    contents.push({
      id: InventoryEventTabs.Mobile_Sales,
      Icon: SaleIcon,
      contentId: ContentId.Sales,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Purchases,
      Icon: PurchaseIcon,
      contentId: ContentId.Purchases,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Notes,
      Icon: PlusIcon,
      contentId: ContentId.Notes,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    return contents;
  }, [
    accordionItemProps?.event?.viagId,
    hasAutoPricingFeature,
    hasConsignorMasterUI,
    hasFulfillmentRulesEventLevelFeature,
    hasMobileInventoryEventPageFeature,
    hasPricingABTestFeature,
    hasPricingV2DemandDashboard,
  ]);

  return (
    <InventoryEventLocalContext.Provider
      value={{
        defaultTab: defaultInventoryTabUserSetting,
        activeTab,
        showMap,
        eventContents,
        marketplaceEventInfo,
        listings,
        compListings: compListingsQuery.data ?? [],
        toggleShowMap,
        refetchMarketplaceEventInfo,
        setActiveTab: onSetInventoryTab,
        onUpdateDefaultType: setDefaultInventoryTabUserSetting,
      }}
    >
      {children}
    </InventoryEventLocalContext.Provider>
  );
};
